import { MapPin, Mail, Phone, Crosshair } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
export default function FindYourWay() {
  return <section data-section-id="223105" id="find-your-way" className="relative w-full min-h-[700px] dark bg-background text-foreground flex items-center overflow-hidden">
      {/* Background Pattern and Decorative Layer */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,var(--primary)_0%,var(--secondary)_45%,transparent_75%)] opacity-20" />
          <div className="absolute inset-0 opacity-15 bg-[linear-gradient(to_right,var(--primary)_1px,transparent_1px),linear-gradient(to_bottom,var(--primary)_1px,transparent_1px)] bg-[size:48px_48px]" />
        </div>
        
        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />

        {/* Location Crosshair / Marker Pin */}
        <div className="absolute top-1/2 left-[70%] -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
          {/* Pulsing Radar Rings */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-24 h-24 rounded-lg border border-primary/30 animate-[ping_3s_cubic-bezier(0,0,0.2,1)_infinite]" />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 rounded-lg border border-primary/40 animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite_1s]" />
          </div>
          
          {/* Main Marker */}
          <div className="relative z-10 w-12 h-12 rounded-lg bg-background/80 backdrop-blur-md flex items-center justify-center border border-primary shadow-lg shadow-primary/20">
            <Crosshair className="w-6 h-6 text-primary" />
          </div>
          
          {/* Line pointer */}
          <div className="w-[2px] h-8 bg-gradient-to-b from-primary to-transparent mt-1" />
          
          {/* Target Base Dot */}
          <div className="w-2 h-2 bg-primary rounded-lg shadow-sm shadow-primary mt-1" />
        </div>
      </div>

      {/* Main Content Info Card */}
      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="max-w-md">
          <Card className="bg-card/60 backdrop-blur-xl border-primary/10 shadow-2xl overflow-hidden relative rounded-2xl">
            {/* Ambient Card Glow */}
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent opacity-50" />
            
            {/* Corner Decorative Accents */}
            <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-primary/30 rounded-tl-2xl" />
            <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-primary/30 rounded-tr-2xl" />
            <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-primary/30 rounded-bl-2xl" />
            <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-primary/30 rounded-br-2xl" />

            <CardHeader className="pb-8">
              <CardTitle className="text-4xl font-default tracking-tight text-foreground">
                Find Your Way
              </CardTitle>
              <CardDescription className="text-primary font-serif italic text-base tracking-normal mt-2">
                Visit M&M Café & Market
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-8">
              {/* Location */}
              <div className="group flex items-start gap-5">
                <div className="mt-1 p-2.5 rounded-lg bg-primary/10 border border-primary/20 text-primary transition-colors group-hover:bg-primary/20 group-hover:border-primary/40">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-xs font-default font-semibold text-muted-foreground uppercase tracking-widest mb-1.5">
                    Our Location
                  </h3>
                  <p className="text-base font-default text-foreground leading-relaxed">
                    786 Enfield St, Suite 1<br />
                    Enfield, CT 06082
                  </p>
                </div>
              </div>

              {/* Email */}
              <div className="group flex items-start gap-5">
                <div className="mt-1 p-2.5 rounded-lg bg-primary/10 border border-primary/20 text-primary transition-colors group-hover:bg-primary/20 group-hover:border-primary/40">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-xs font-default font-semibold text-muted-foreground uppercase tracking-widest mb-1.5">
                    Say Hello
                  </h3>
                  <Link to="mailto:mmcafeandmarket@gmail.com" className="text-base font-default text-foreground hover:text-primary transition-colors inline-block">
                    mmcafeandmarket@gmail.com
                  </Link>
                </div>
              </div>

              {/* Phone */}
              <div className="group flex items-start gap-5">
                <div className="mt-1 p-2.5 rounded-lg bg-primary/10 border border-primary/20 text-primary transition-colors group-hover:bg-primary/20 group-hover:border-primary/40">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-xs font-default font-semibold text-muted-foreground uppercase tracking-widest mb-1.5">
                    Call Us
                  </h3>
                  <Link to="tel:+18608355222" className="text-base font-default text-foreground hover:text-primary transition-colors inline-block">
                    (860) 835-5222
                  </Link>
                </div>
              </div>
            </CardContent>

            <CardFooter className="pt-4 pb-8">
              <Button className="w-full font-default font-semibold uppercase tracking-widest text-xs h-12 border-primary/30 hover:border-primary hover:bg-primary/10 hover:text-primary transition-all duration-300 rounded-lg" variant="outline">
                Get Directions
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    </section>;
}